import { create } from "zustand";
import axios from "axios";
import {
  imageSize,
  isSupportedVideoFile,
  SvgTooLargeError,
  uploadImage,
  uploadVideoWithPoster,
  type VideoResume,
} from "../api/upload";
import { chatMediaOf, chatVideoMediaOf, type ChatMedia } from "../utils/chatMedia";
import logger from "../lib/logger";
import {
  planVideo,
  prepareVideo,
  recordUplinkMbps,
  VideoTooLargeError,
  type PlannedVideo,
  type PreparedVideo,
} from "../media/videoPrep";

/**
 * The app-wide video send queue (spec 2026-10-23 "Behaviour", web): compression and upload run here,
 * outside the chat and editor components, so switching conversation or closing a panel keeps a send
 * going. One item at a time: waiting → compressing → uploading → done | error | cancelled. The wall
 * editor takes finished videos through `uploadQueue.onComplete`; chat sends albums (photos and
 * videos, one message) through `enqueueAlbum` / `onAlbumComplete`.
 */

/** One item of a chat album, as staged (tray order). */
export interface ChatAlbumSlotSpec {
  kind: "IMAGE" | "VIDEO";
  fileName: string;
}

export type UploadTarget =
  | { kind: "wall"; draftId: string; itemKey: string }
  /** One chat message of 1-5 photos/videos (chat albums spec 2026-10-24): published once every slot is done. */
  | { kind: "chatAlbum"; convId: string; localId: string; slots: ChatAlbumSlotSpec[] };

export type UploadSlotPhase = "waiting" | "compressing" | "uploading" | "done" | "error";

/** A chat album slot as it stands (UploadItem.slots, same order as target.slots). */
export interface UploadSlot {
  kind: "IMAGE" | "VIDEO";
  fileName: string;
  phase: UploadSlotPhase;
  /** 0..1 of the current phase. */
  progress: number;
  /**
   * Object URL shown while sending: the photo itself, or the video's poster once taken. Revoked (and
   * cleared) when the album turns "Not sent", is removed, or its failed publish expires.
   */
  previewUrl?: string;
  /** Photos: measured once when the slot is created (and passed to the upload); videos: as encoded. */
  width?: number;
  height?: number;
  durationMs?: number;
  /** done: what the message publishes for this slot. */
  media?: ChatMedia;
  errorCode?: UploadErrorCode;
  errorDetail?: "BROWSER_LIMIT";
}

export type UploadPhase = "waiting" | "compressing" | "uploading" | "done" | "error" | "cancelled";

/**
 * TOO_LARGE, UNSUPPORTED_TYPE and REJECTED can never go: "Not sent" at once, no retry. REJECTED = file-stc
 * refused an image (413 over its cap, e.g. an SVG over 1 MiB; 422 not usable, e.g. a hostile SVG).
 */
export type UploadErrorCode = "TOO_LARGE" | "UNSUPPORTED_TYPE" | "REJECTED" | "FAILED";

/** file-stc refused the image itself (413 / 422), or would (an SVG over 1 MiB): sending it again cannot help. */
export function isRejectedImage(err: unknown): boolean {
  if (err instanceof SvgTooLargeError) return true; // refused here, before file-stc would 413 it
  if (!axios.isAxiosError(err)) return false;
  const status = err.response?.status;
  return status === 413 || status === 422;
}

export interface UploadItem {
  id: string;
  target: UploadTarget;
  fileName: string;
  phase: UploadPhase;
  /** 0..1 of the current phase (compressing: encode progress; uploading: video bytes). */
  progress: number;
  bytesSent: number;
  bytesTotal: number;
  /** Object URL of the poster taken from the source, shown at once. */
  posterUrl?: string;
  fileId?: string;
  thumbnailFileId?: string;
  width?: number;
  height?: number;
  durationMs?: number;
  mime?: string;
  cantPlayHint: boolean;
  errorCode?: UploadErrorCode;
  /** TOO_LARGE only: BROWSER_LIMIT = over 1 GB and this browser cannot compress it ("Video is too large to send from this browser"). */
  errorDetail?: "BROWSER_LIMIT";
  /** When the item failed (ms epoch); retry is allowed for RETRY_WINDOW_MS after it. */
  failedAt?: number;
  /** Failed and past the retry window (or not retryable): shows "Not sent", no retry. */
  notSent?: boolean;
  /**
   * chatAlbum only: each slot's state. The item-level phase/progress/fileName/bytes follow the slot
   * being worked on (the queue toast reads them); the item fails when any slot failed.
   */
  slots?: UploadSlot[];
}

/** What a target's owner gets once the video is confirmed READY. */
export interface VideoCompletion {
  id: string;
  target: UploadTarget;
  fileId: string;
  thumbnailFileId: string;
  width: number;
  height: number;
  durationMs: number;
  mime: string;
  cantPlayHint: boolean;
}

/** What the chat album's owner gets once every slot is done: the media in slot order. */
export interface AlbumCompletion {
  id: string;
  target: Extract<UploadTarget, { kind: "chatAlbum" }>;
  media: ChatMedia[];
}

export const RETRY_WINDOW_MS = 10 * 60 * 1000;

interface UploadQueueState {
  items: Record<string, UploadItem>;
  /** Enqueue order. */
  order: string[];
}

export const useUploadQueueStore = create<UploadQueueState>(() => ({ items: {}, order: [] }));

/** Not reactive: one video's bytes and resume point (a video item, or an album's video slot). */
interface VideoWork {
  file: File | null;
  planned?: PlannedVideo;
  prepared?: PreparedVideo;
  resume?: VideoResume;
  /** Album photo slots: its size, measured once at enqueue. */
  size?: Promise<{ width?: number; height?: number }>;
}

/** Not reactive: the bytes and controllers behind each item. */
interface Work extends VideoWork {
  /** chatAlbum: each slot's bytes, in slot order. */
  slots?: VideoWork[];
  ctrl?: AbortController;
  expiry?: ReturnType<typeof setTimeout>;
  delivered: boolean;
}
type VideoKind = Exclude<UploadTarget["kind"], "chatAlbum">;
const work = new Map<string, Work>();
const handlers: Record<VideoKind, Set<(c: VideoCompletion) => void>> = {
  wall: new Set(),
};
const completions = new Map<string, VideoCompletion>();
const albumHandlers = new Set<(c: AlbumCompletion) => void>();
const albumCompletions = new Map<string, AlbumCompletion>();
let running: string | null = null;
let seq = 0;

const ACTIVE: UploadPhase[] = ["waiting", "compressing", "uploading"];
export const isActive = (item: UploadItem): boolean => ACTIVE.includes(item.phase);

/** An errored item may be retried within RETRY_WINDOW_MS of its failure (owner ruling). */
export function canRetry(item: UploadItem | undefined, now = Date.now()): boolean {
  return (
    !!item &&
    item.phase === "error" &&
    !item.notSent &&
    item.errorCode !== "TOO_LARGE" &&
    item.errorCode !== "UNSUPPORTED_TYPE" &&
    item.errorCode !== "REJECTED" &&
    item.failedAt != null &&
    now - item.failedAt <= RETRY_WINDOW_MS
  );
}

function patch(id: string, p: Partial<UploadItem>): void {
  useUploadQueueStore.setState((s) => {
    const cur = s.items[id];
    return cur ? { items: { ...s.items, [id]: { ...cur, ...p } } } : s;
  });
}

function patchSlot(id: string, index: number, p: Partial<UploadSlot>): void {
  useUploadQueueStore.setState((s) => {
    const cur = s.items[id];
    if (!cur?.slots?.[index]) return s;
    const slots = cur.slots.map((sl, i) => (i === index ? { ...sl, ...p } : sl));
    return { items: { ...s.items, [id]: { ...cur, slots } } };
  });
}

const getItem = (id: string) => useUploadQueueStore.getState().items[id];

function releaseVideo(w: VideoWork): void {
  void w.prepared?.dispose().catch(() => {});
  w.prepared = undefined;
  w.planned = undefined;
  w.file = null;
  w.resume = undefined;
  w.size = undefined;
}

const revoke = (url: string | undefined) => {
  if (url && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(url);
};

/** An album's local previews are let go (they pin the original files): revoked and cleared. */
function releasePreviews(id: string): void {
  const item = getItem(id);
  if (!item?.slots?.some((sl) => sl.previewUrl)) return;
  for (const sl of item.slots) revoke(sl.previewUrl);
  patch(id, { slots: item.slots.map((sl) => ({ ...sl, previewUrl: undefined })) });
}

function release(id: string): void {
  const w = work.get(id);
  if (!w) return;
  if (w.expiry) clearTimeout(w.expiry);
  releaseVideo(w);
  w.slots?.forEach(releaseVideo);
}

const objectUrl = (b: Blob): string | undefined =>
  typeof URL.createObjectURL === "function" ? URL.createObjectURL(b) : undefined;

// --- beforeunload ----------------------------------------------------------

let guardOn = false;
function onBeforeUnload(e: BeforeUnloadEvent): void {
  e.preventDefault();
  e.returnValue = ""; // older browsers need a value to show the prompt
}
function syncGuard(): void {
  if (typeof window === "undefined") return;
  const active = Object.values(useUploadQueueStore.getState().items).some(isActive);
  if (active && !guardOn) window.addEventListener("beforeunload", onBeforeUnload);
  if (!active && guardOn) window.removeEventListener("beforeunload", onBeforeUnload);
  guardOn = active;
}
useUploadQueueStore.subscribe(syncGuard);

// --- hand-off --------------------------------------------------------------

function deliverAlbum(c: AlbumCompletion): void {
  const w = work.get(c.id);
  if (albumHandlers.size === 0 || !w || w.delivered) return;
  w.delivered = true;
  albumCompletions.delete(c.id);
  for (const h of albumHandlers) {
    try {
      h(c);
    } catch (err) {
      logger.error("[uploadQueue] album completion handler failed", err);
    }
  }
}

function deliver(c: VideoCompletion): void {
  const w = work.get(c.id);
  if (c.target.kind === "chatAlbum") return;
  const hs = handlers[c.target.kind];
  if (hs.size === 0 || !w || w.delivered) return;
  w.delivered = true;
  completions.delete(c.id);
  for (const h of hs) {
    try {
      h(c);
    } catch (err) {
      logger.error("[uploadQueue] completion handler failed", err);
    }
  }
}

// --- the runner ------------------------------------------------------------

function pump(): void {
  if (running) return;
  const { order, items } = useUploadQueueStore.getState();
  const next = order.find((id) => items[id]?.phase === "waiting");
  if (!next) return;
  running = next;
  void runItem(next).finally(() => {
    running = null;
    pump();
  });
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

interface VideoResult {
  fileId: string;
  thumbnailFileId: string;
  width: number;
  height: number;
  durationMs: number;
  mime: string;
  cantPlayHint: boolean;
}

/**
 * One video: plan (unless the tray already did), compress (unless already compressed), upload with its
 * poster (resuming a cut-off upload). `report` gets the same fields a video item shows.
 */
async function runVideo(
  w: VideoWork,
  signal: AbortSignal,
  report: (p: Partial<UploadItem>) => void,
): Promise<VideoResult> {
  if (!w.prepared) {
    report({ phase: "compressing", progress: 0 });
    if (!w.planned) {
      if (!w.file) throw new Error("source released");
      w.planned = await planVideo(w.file);
      if (signal.aborted) throw new DOMException("Aborted", "AbortError");
      report({ posterUrl: objectUrl(w.planned.poster.blob), durationMs: w.planned.probe.source.durationMs });
    }
    w.prepared = await prepareVideo(w.planned, (p) => report({ progress: p }), signal);
  }
  const prepared = w.prepared;
  const poster = w.planned!.poster;
  report({
    phase: "uploading",
    progress: 0,
    bytesSent: 0,
    bytesTotal: prepared.blob.size,
    width: prepared.width,
    height: prepared.height,
    durationMs: prepared.durationMs,
    mime: prepared.mime,
    cantPlayHint: prepared.cantPlayHint,
  });

  let last = { t: Date.now(), bytes: 0 };
  const res = await uploadVideoWithPoster(
    prepared.blob,
    poster.blob,
    {
      filename: prepared.filename,
      mime: prepared.mime,
      durationMs: prepared.durationMs || undefined,
      width: prepared.width || undefined,
      height: prepared.height || undefined,
      posterWidth: poster.width,
      posterHeight: poster.height,
    },
    {
      signal,
      resume: w.resume,
      onResume: (r) => {
        w.resume = r;
      },
      onProgress: (sent, total) => {
        const now = Date.now();
        const dt = now - last.t;
        if (dt >= 1000 && sent > last.bytes) {
          recordUplinkMbps(((sent - last.bytes) * 8) / 1e6 / (dt / 1000));
          last = { t: now, bytes: sent };
        }
        report({ bytesSent: sent, bytesTotal: total, progress: total > 0 ? sent / total : 0 });
      },
    },
  );
  if (signal.aborted) throw new DOMException("Aborted", "AbortError");
  return {
    fileId: res.video.id,
    thumbnailFileId: res.posterId,
    width: prepared.width,
    height: prepared.height,
    durationMs: prepared.durationMs,
    mime: prepared.mime,
    cantPlayHint: prepared.cantPlayHint,
  };
}

async function runItem(id: string): Promise<void> {
  const w = work.get(id);
  if (!w) return;
  if (getItem(id)?.target.kind === "chatAlbum") return runAlbum(id, w);
  const ctrl = new AbortController();
  w.ctrl = ctrl;
  const { signal } = ctrl;
  try {
    const v = await runVideo(w, signal, (p) => patch(id, p));
    patch(id, { phase: "done", progress: 1, fileId: v.fileId, thumbnailFileId: v.thumbnailFileId });
    const item = getItem(id)!;
    const completion: VideoCompletion = { id, target: item.target, ...v };
    release(id);
    completions.set(id, completion);
    deliver(completion);
  } catch (err) {
    if (signal.aborted || isAbort(err)) {
      patch(id, { phase: "cancelled" });
      release(id);
      return;
    }
    const tooLarge = err instanceof VideoTooLargeError;
    logger.error("[uploadQueue] video send failed", err);
    patch(id, {
      phase: "error",
      errorCode: tooLarge ? "TOO_LARGE" : "FAILED",
      errorDetail: tooLarge && err.reason === "BROWSER_LIMIT" ? "BROWSER_LIMIT" : undefined,
      failedAt: Date.now(),
      notSent: tooLarge || undefined,
    });
    if (tooLarge) release(id);
    else w.expiry = setTimeout(() => expire(id), RETRY_WINDOW_MS + 1);
  } finally {
    w.ctrl = undefined;
  }
}

/**
 * A chat album: every slot not done yet, in order (photos upload and confirm, videos compress and
 * upload). A failed slot does not stop the others, so a retry redoes only the failed ones. Done when
 * every slot is; failed (retry for RETRY_WINDOW_MS) when any is.
 */
async function runAlbum(id: string, w: Work): Promise<void> {
  const ctrl = new AbortController();
  w.ctrl = ctrl;
  const { signal } = ctrl;
  try {
    const slots = getItem(id)?.slots ?? [];
    for (let i = 0; i < slots.length; i++) {
      if (getItem(id)?.slots?.[i]?.phase !== "waiting") continue;
      const sw = w.slots![i];
      // The item follows the slot being worked on (the queue toast shows it).
      const report = (p: Partial<UploadItem>) => {
        const { posterUrl, ...rest } = p;
        patch(id, { ...rest, fileName: slots[i].fileName });
        const sp: Partial<UploadSlot> = {};
        if (p.phase === "compressing" || p.phase === "uploading") sp.phase = p.phase;
        if (p.progress != null) sp.progress = p.progress;
        if (posterUrl) sp.previewUrl = posterUrl;
        if (p.width) sp.width = p.width;
        if (p.height) sp.height = p.height;
        if (p.durationMs) sp.durationMs = p.durationMs;
        patchSlot(id, i, sp);
      };
      try {
        let media: ChatMedia;
        if (slots[i].kind === "VIDEO") {
          if (sw.file && !isSupportedVideoFile(sw.file)) throw new UnsupportedVideoError();
          media = chatVideoMediaOf(await runVideo(sw, signal, report));
        } else {
          if (!sw.file) throw new Error("source released");
          report({ phase: "uploading", progress: 0, bytesSent: 0, bytesTotal: sw.file.size });
          // Measured once, at enqueue: the upload takes the same size instead of decoding again.
          const size = (await sw.size) ?? {};
          const info = await uploadImage(sw.file, {
            ...size,
            signal,
            onProgress: (sent, total) =>
              report({ bytesSent: sent, bytesTotal: total, progress: total > 0 ? sent / total : 0 }),
          });
          if (signal.aborted) throw new DOMException("Aborted", "AbortError");
          media = chatMediaOf(info);
        }
        patchSlot(id, i, { phase: "done", progress: 1, media });
        releaseVideo(sw);
      } catch (err) {
        if (signal.aborted || isAbort(err)) throw err;
        const tooLarge = err instanceof VideoTooLargeError;
        logger.error("[uploadQueue] album slot failed", err);
        patchSlot(id, i, {
          phase: "error",
          errorCode: tooLarge
            ? "TOO_LARGE"
            : err instanceof UnsupportedVideoError
              ? "UNSUPPORTED_TYPE"
              : slots[i].kind === "IMAGE" && isRejectedImage(err)
                ? "REJECTED"
                : "FAILED",
          errorDetail: tooLarge && err.reason === "BROWSER_LIMIT" ? "BROWSER_LIMIT" : undefined,
        });
      }
    }
    const after = getItem(id)?.slots ?? [];
    if (after.every((sl) => sl.phase === "done")) {
      patch(id, { phase: "done", progress: 1 });
      const item = getItem(id)!;
      const completion: AlbumCompletion = {
        id,
        target: item.target as AlbumCompletion["target"],
        media: after.map((sl) => sl.media!),
      };
      release(id);
      albumCompletions.set(id, completion);
      deliverAlbum(completion);
      return;
    }
    // A slot that can never go (too large, unsupported type) makes the whole album "Not sent" at once.
    const fatal = after.find(
      (sl) => sl.errorCode === "TOO_LARGE" || sl.errorCode === "UNSUPPORTED_TYPE" || sl.errorCode === "REJECTED",
    );
    patch(id, {
      phase: "error",
      errorCode: fatal?.errorCode ?? "FAILED",
      errorDetail: fatal?.errorDetail,
      failedAt: Date.now(),
      notSent: fatal ? true : undefined,
    });
    if (fatal) {
      release(id);
      releasePreviews(id);
    } else w.expiry = setTimeout(() => expire(id), RETRY_WINDOW_MS + 1);
  } catch (err) {
    if (!(signal.aborted || isAbort(err))) logger.error("[uploadQueue] album send failed", err);
    patch(id, { phase: "cancelled" });
    release(id);
  } finally {
    w.ctrl = undefined;
  }
}

/** Past the retry window: "Not sent", the bytes (and an album's previews) are let go. */
function expire(id: string): void {
  const item = getItem(id);
  if (!item || item.phase !== "error") return;
  patch(id, { notSent: true });
  release(id);
  releasePreviews(id);
}

/** An album video slot that is not MP4, MOV or WebM: refused before any upload. */
class UnsupportedVideoError extends Error {
  constructor() {
    super("unsupported video type");
    this.name = "UnsupportedVideoError";
  }
}

// --- public API ------------------------------------------------------------

export const uploadQueue = {
  enqueueVideo({ file, target }: { file: File; target: UploadTarget }): string {
    const id = `vu-${Date.now().toString(36)}-${(seq++).toString(36)}`;
    work.set(id, { file, delivered: false });
    useUploadQueueStore.setState((s) => ({
      items: {
        ...s.items,
        [id]: {
          id,
          target,
          fileName: file.name,
          phase: "waiting",
          progress: 0,
          bytesSent: 0,
          bytesTotal: file.size,
          cantPlayHint: false,
        },
      },
      order: [...s.order, id],
    }));
    pump();
    return id;
  },

  /**
   * A chat album: one item whose slots (photos and videos, tray order) upload one after another; the
   * owner publishes once on `onAlbumComplete`. A video the tray already probed passes its `planned`.
   */
  enqueueAlbum({
    convId,
    localId,
    files,
  }: {
    convId: string;
    localId: string;
    files: { file: File; kind: "IMAGE" | "VIDEO"; planned?: PlannedVideo | null }[];
  }): string {
    const id = `au-${Date.now().toString(36)}-${(seq++).toString(36)}`;
    const specs: ChatAlbumSlotSpec[] = files.map((f) => ({ kind: f.kind, fileName: f.file.name }));
    const slots: UploadSlot[] = files.map((f) => {
      const preview = f.kind === "IMAGE" ? f.file : f.planned?.poster.blob;
      return {
        kind: f.kind,
        fileName: f.file.name,
        phase: "waiting",
        progress: 0,
        previewUrl: preview ? objectUrl(preview) : undefined,
        durationMs: f.planned?.probe.source.durationMs || undefined,
      };
    });
    const slotWork: VideoWork[] = files.map((f) => ({
      file: f.file,
      planned: f.planned ?? undefined,
      size: f.kind === "IMAGE" ? imageSize(f.file) : undefined,
    }));
    work.set(id, { file: null, delivered: false, slots: slotWork });
    useUploadQueueStore.setState((s) => ({
      items: {
        ...s.items,
        [id]: {
          id,
          target: { kind: "chatAlbum", convId, localId, slots: specs },
          fileName: files[0]?.file.name ?? "",
          phase: "waiting",
          progress: 0,
          bytesSent: 0,
          bytesTotal: files.reduce((n, f) => n + f.file.size, 0),
          cantPlayHint: false,
          slots,
        },
      },
      order: [...s.order, id],
    }));
    // Photo sizes land on their slots as soon as they are known (the sending album's layout).
    slotWork.forEach((sw, i) => {
      void sw.size?.then(({ width, height }) => {
        if (width && height) patchSlot(id, i, { width, height });
      });
    });
    pump();
    return id;
  },

  /** Cancels the encode (Conversion.cancel) or aborts the upload (multipart parts stop). */
  cancel(id: string): void {
    const item = getItem(id);
    if (!item) return;
    if (item.phase === "waiting" || item.phase === "error") {
      patch(id, { phase: "cancelled" });
      release(id);
      return;
    }
    work.get(id)?.ctrl?.abort();
  },

  /**
   * Tries a failed item again (within 10 minutes of the failure), resuming its upload. An album
   * retries only its failed slots.
   */
  retry(id: string): boolean {
    const item = getItem(id);
    if (!canRetry(item)) {
      if (item?.phase === "error" && !item.notSent) expire(id);
      return false;
    }
    const w = work.get(id);
    if (w?.expiry) clearTimeout(w.expiry);
    patch(id, {
      phase: "waiting",
      errorCode: undefined,
      failedAt: undefined,
      progress: 0,
      slots: item!.slots?.map((sl) =>
        sl.phase === "error" ? { ...sl, phase: "waiting", progress: 0, errorCode: undefined } : sl,
      ),
    });
    pump();
    return true;
  },

  /** Drops a finished, cancelled or failed item from the store. */
  remove(id: string): void {
    const item = getItem(id);
    if (!item || isActive(item)) return;
    release(id);
    work.delete(id);
    completions.delete(id);
    albumCompletions.delete(id);
    revoke(item.posterUrl);
    for (const sl of item.slots ?? []) revoke(sl.previewUrl);
    useUploadQueueStore.setState((s) => {
      const items = { ...s.items };
      delete items[id];
      return { items, order: s.order.filter((x) => x !== id) };
    });
  },

  /**
   * Per-kind completion of single videos: the wall editor fills its item. Completions that arrived before any handler are delivered on registration, once.
   */
  onComplete(kind: VideoKind, handler: (c: VideoCompletion) => void): () => void {
    handlers[kind].add(handler);
    for (const c of [...completions.values()]) if (c.target.kind === kind) deliver(c);
    return () => {
      handlers[kind].delete(handler);
    };
  },

  /** Lets an album's local previews go (its failed publish expired: "Not sent"). */
  releasePreviews(id: string): void {
    releasePreviews(id);
  },

  /** Chat albums: every slot done. Completions that arrived before any handler come on registration. */
  onAlbumComplete(handler: (c: AlbumCompletion) => void): () => void {
    albumHandlers.add(handler);
    for (const c of [...albumCompletions.values()]) deliverAlbum(c);
    return () => {
      albumHandlers.delete(handler);
    };
  },

  get(id: string): UploadItem | undefined {
    return getItem(id);
  },
};

export function resetUploadQueueForTests(): void {
  for (const w of work.values()) {
    w.ctrl?.abort();
    if (w.expiry) clearTimeout(w.expiry);
  }
  work.clear();
  completions.clear();
  albumCompletions.clear();
  albumHandlers.clear();
  handlers.wall.clear();
  running = null;
  useUploadQueueStore.setState({ items: {}, order: [] });
}
